import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './FinanceTable.module.scss'
import { IpRestS } from '../../../../rest/ipRest-service';
import { RestS } from '../../../../rest/rest-service';
import { ExpandedState, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import moment from 'moment';
import { MdArrowDropDown } from 'react-icons/md';
import { formatNumberWithComma, formatNumberWithCommasAndUnits, getLast12CYMonthsArr, isNumeric } from '../../../../components/functions/utilities';
import { GoPencil as PencilIcon } from "react-icons/go";
import { useNavigate } from 'react-router';
import MeritoButton from '../../../../components/common/MeritoButtons/MeritoButtons';
import { GoDotFill as DotIcon } from "react-icons/go";
import { AiFillQuestionCircle as MissingCostIcon } from "react-icons/ai";
import { OverlayTrigger } from 'react-bootstrap';
import { RenderIBtnTooltip, RenderSideBarTooltip, RenderTooltip } from '../../../../components/common/RenderTooltip';
import { PnlFsMetricNames, PnlFsNames, pnlFormat } from '../../../../interfaces/interfaces';
import { MdInfo as InfoIcon } from 'react-icons/md';
import { RiAddFill as AddIcon } from 'react-icons/ri';
import { HiArrowNarrowDown, HiArrowNarrowUp, HiArrowNarrowDown as ScrollToBottomIcon } from 'react-icons/hi';
import usePageTitle from '../../../../components/Hooks/usePageTitle';
import { RiAlertFill as AlertIcon } from 'react-icons/ri';
import { MdOutlineError as ErrorIcon } from 'react-icons/md';


type Props = {
    tableData: any,
    handlePnlFormatChange: (pnlFormat: pnlFormat) => void,
    missingProductDetails: {
        productCount: number;
        missingCostCount: number;
    }
}


const percentageColumns = ["Monthly Growth %", "Gross Profit %", "CM1 %", "CM2 %", "EBITDA Margin", "Net Cash Margin"];
const ignoreDarkenBorder = [PnlFsNames.percGrowth, PnlFsNames.netSales]
const applyCostMissingParams = new Set([PnlFsNames.cogs])
const extraGrowthColumns = [PnlFsNames.ebitdaMargin, PnlFsNames.netCashMargin, PnlFsNames.cm1Perc, PnlFsNames.cm2Perc, PnlFsNames.grossProfitPerc, PnlFsNames.percGrowth];
const iBtnColumns = {
    "Net Sales": {
        title: "Net Sales",
        subTitle: <>Reflects your brand’s topline growth.</>
    },
    "Gross Profit": {
        title: "Gross Profit",
        subTitle: <>It is the remaining amount from your sales revenue deducting the cost of goods sold (COGS).</>
    },
    "CM1": {
        title: "CM1",
        subTitle: <>It shows how well you manage shipping and packaging costs relative to gross profit.</>
    },
    "Platform Commission": {
        title: "Platform Commission",
        subTitle: <>It’s the fee you pay for using the platform to sell your products excluding the shipping.</>
    },
    "CM2": {
        title: "CM2",
        subTitle: <>
            It reveals your profit margins, which helps in understanding the effectiveness of sales strategies and overall spending.
        </>
    },
    [PnlFsNames.ebitda]: {
        title: "EBITDA",
        subTitle: <>Indicates your operational cash flow and financial health.</>
    },
    [PnlFsNames.netCash]: {
        title: 'Net Cash',
        subTitle: <>Reflects cash available after loan payments, showing the liquidity that can be reinvested or saved.</>
    }
};

const separatorRows = ["Packaging Cost", "Ad Spend", PnlFsNames.operatingExpenses, PnlFsNames.loanServicing];

const highlightRows = [PnlFsNames.grossProfitPerc, PnlFsNames.cm1Perc, PnlFsNames.cm2Perc, PnlFsNames.ebitdaMargin, PnlFsNames.netCashMargin]

const redirectMappings = {
    "GST Collected": "Taxes"
}

const LAKHS = 100000

// Utility function to debounce another function
function debounce(func, wait) {
    let timeout;
    return function (...args) {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), wait);
    };
}

const FinanceTable = ({ tableData, handlePnlFormatChange, missingProductDetails }: Props) => {
    const columnHelper = createColumnHelper();
    const navigate = useNavigate();

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [growth, setGrowth] = useState({})

    const [showShadow, setShowShadow] = useState(false);
    const tableContainerRef = useRef(null);

    const firstColumnRef = useRef(null);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [availableWidth, setAvailableWidth] = useState(0);

    const [showScrollToBottomBtn, setShowScrollToBottomBtn] = useState(false);
    const pageTitle = usePageTitle('P&L Tracker | Merito');

    useEffect(() => {
        return () => {
            pageTitle('')
        }
    }, [])

    // Debounced version of handleScroll
    const handleScroll = useCallback(() => {
        const isScrolled = tableContainerRef.current.scrollLeft > 0;
        setShowShadow(isScrolled);

    }, []);

    useEffect(() => {

        const container = tableContainerRef.current;
        container.addEventListener('scroll', handleScroll);

        // handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [handleScroll]);

    const formatTableData = useMemo(() => {
        let expandedInitialState = {};
        let rowConfigs = {};
        const last3Months = new Set(getLast12CYMonthsArr().slice(0, 3));
        const pastLast3Months = new Set(getLast12CYMonthsArr().slice(3, 6));
        // console.log('pastLast3Months', pastLast3Months);
        const categoryObj = tableData?.financeCategories.reduce((prev, curVal) => {
            prev[curVal.financeStatementId] = curVal;
            return prev;
        }, {});

        let totalCount = 0;
        let countInLakhs = 0;
        let countInThousands = 0;

        let growth = {}

        const formattedBodyData = tableData?.tableData.reduce((prev, curVal, index) => {

            // console.log(curVal, curVal?.rowValues[0]?.value, curVal?.rowValues[1]?.value)
            const growthNumber: any = curVal?.rowValues[0]?.value == 0 ? 0 : ((((curVal?.rowValues[0]?.value - curVal?.rowValues[1]?.value) / Math.abs(curVal?.rowValues[1]?.value)) * 100)).toFixed(1)
            // console.log(growthNumber)
            // const growthNumberRounded = Math.abs(growthNumber) > 10 ? Math.round(growthNumber) : growthNumber
            const growthNumberRounded = parseFloat(growthNumber)

            let rowValuesObj = {};
            if (curVal.rowValues.length) {
                rowValuesObj = curVal.rowValues.reduce((rowValuesPrev, rowValuesCurVal) => {
                    rowValuesPrev[rowValuesCurVal.monthYear] = {
                        ...rowValuesCurVal,
                    }
                    return rowValuesPrev;
                }, {});
            }
            // console.log('rowValuesObj', rowValuesObj);
            const isAvgColumns = percentageColumns.includes(curVal?.name) ? true : false;
            let totalRows = 0;
            let totalOfRows = 0;
            let totalOf3Months = 0;
            let pastTotalOf3Months = 0;

            let rowIsAllNull = true;
            const formattedRowValues = tableData?.months.reduce((prevMonths, curValMonths) => {
                if (rowValuesObj[curValMonths] != undefined) {
                    const rowNumberValue = isNumeric(`${rowValuesObj[curValMonths]['value']}`) ? Number(rowValuesObj[curValMonths]['value']) : 0;
                    // if (isAvgColumns) {

                    totalOfRows += rowNumberValue;
                    if (last3Months.has(curValMonths)) {
                        totalOf3Months += rowNumberValue;
                    }
                    if (pastLast3Months.has(curValMonths)) {
                        pastTotalOf3Months += rowNumberValue;
                    }
                    // }
                    prevMonths[curValMonths] = rowNumberValue;

                    if (rowNumberValue) {
                        rowIsAllNull = false;
                        totalRows += 1;
                    }

                    if (rowNumberValue >= 10000 && !isAvgColumns) {
                        countInLakhs += 1;
                    }
                    if (rowNumberValue < 10000 && !isAvgColumns) {
                        countInThousands += 1
                    }

                    if (!isAvgColumns) {
                        totalCount += 1;
                    }


                } else {
                    prevMonths[curValMonths] = "";
                }
                return prevMonths;
            }, {});

            const last3MonthsGrowth: any = pastTotalOf3Months == 0 ? 0 : parseFloat(((((totalOf3Months - pastTotalOf3Months) / Math.abs(pastTotalOf3Months)) * 100)).toFixed(1))

            if (extraGrowthColumns.includes(curVal.name)) {
                // console.log('last 3 months growth', last3MonthsGrowth, totalOf3Months, pastTotalOf3Months)
                if (growth[curVal.name]) {
                    growth = {
                        ...growth,
                        [curVal.name]: {
                            ...growth[curVal.name],
                            [curVal?.rowValues[0]?.monthYear]: growthNumberRounded,
                            'Last 3 Months': last3MonthsGrowth
                        }
                    }
                } else {
                    growth = {
                        ...growth,
                        [curVal.name]: {
                            [curVal?.rowValues[0]?.monthYear]: growthNumberRounded,
                            'Last 3 Months': last3MonthsGrowth
                        }
                    }
                }
            }


            setGrowth(growth)



            // console.log('formattedRowValues', formattedRowValues);
            let subRowsBody = [];
            let subRowConfig = [];
            if (curVal.isExpandable) {
                subRowsBody = curVal.subRows.reduce((subRowsPrev, subRowsCurVal) => {
                    let subRowValuesObj = {};
                    let availableMonthsData = [];
                    if (subRowsCurVal.rowValues.length) {
                        subRowValuesObj = subRowsCurVal.rowValues.reduce((subRowValuesPrev, subrowValuesCur) => {
                            if (subrowValuesCur.apiCost) availableMonthsData.push(subrowValuesCur.metricMonthYear);
                            subRowValuesPrev[subrowValuesCur.metricMonthYear] = {
                                ...subrowValuesCur,
                            }
                            return subRowValuesPrev;
                        }, {});
                    }
                    const isAvgSubColumns = percentageColumns.includes(subRowsCurVal?.metricName) ? true : false;
                    let totalSubRows = 0;
                    let totalOfSubRows = 0;
                    let totalof3MonthsSubRows = 0;
                    const formattedSubRowValues = tableData?.months.reduce((prevSubRowMonths, curSubRowMonths) => {
                        if (subRowValuesObj[curSubRowMonths] != undefined) {
                            let finalCost = subRowValuesObj[curSubRowMonths]['inEffectValue'] == "api_cost" ? subRowValuesObj[curSubRowMonths]['apiCost'] : subRowValuesObj[curSubRowMonths]['updatedCost'];
                            const subRowNumberValue = isNumeric(`${finalCost}`) ? Number(finalCost) : 0;
                            totalSubRows += 1;
                            totalOfSubRows +=
                                prevSubRowMonths[curSubRowMonths] = subRowNumberValue;

                            if (last3Months.has(curSubRowMonths)) {
                                totalof3MonthsSubRows += subRowNumberValue;
                            }

                            if (subRowNumberValue >= 10000 && !isAvgSubColumns) {
                                countInLakhs += 1;
                            }

                            if (subRowNumberValue < 10000 && !isAvgSubColumns) {
                                countInThousands += 1
                            }

                            if (!isAvgSubColumns) {
                                totalCount += 1;
                            }
                        } else {
                            prevSubRowMonths[curSubRowMonths] = "";
                        }
                        return prevSubRowMonths;
                    }, {});
                    subRowsPrev.push({
                        title: subRowsCurVal?.metricName,
                        total: isAvgSubColumns ? Number(totalOfSubRows / totalOfSubRows).toFixed(1) : totalOfSubRows,
                        total3Months: isAvgSubColumns ? Number(totalof3MonthsSubRows / 3).toFixed(1) : totalof3MonthsSubRows,
                        ...formattedSubRowValues,
                    })
                    let searchParams = "?";
                    if (subRowsCurVal.categoryName) {
                        searchParams += `section=${subRowsCurVal.categoryName}`;
                    }
                    searchParams += searchParams.length > 0 ? "&" : "";

                    searchParams += `card=${subRowsCurVal?.metricName}`

                    subRowConfig.push({
                        isEditable: subRowsCurVal.isEditable,
                        redirectLink: `/pnltracker/edit/${subRowsCurVal?.feTab.replace(/\s+/g, '-').toLowerCase()}${searchParams}`,
                        isMissingCost: Object.keys(subRowValuesObj).length == 0 && subRowsCurVal?.isEditable ? true : false,
                        dataAvailableError: subRowsCurVal.metricName == PnlFsMetricNames.amazonAds && availableMonthsData.length > 0 && availableMonthsData.length <= 3 ? `Data only available for ${availableMonthsData.length} months` : null
                    })
                    return subRowsPrev;
                }, [])
            }
            prev.push({
                title: curVal?.name,
                ...formattedRowValues,
                total: isAvgColumns ? totalRows == 0 ? 0 : (Number(totalOfRows / totalRows)).toFixed(1) : totalOfRows,
                total3Months: isAvgColumns ? (Number(totalOf3Months / 3)).toFixed(1) : totalOf3Months,
                subRows: subRowsBody,
            })
            if (curVal?.isExpandable) {
                expandedInitialState[index] = false;
            }

            let rowSearchParams = "?";
            if (rowSearchParams) {
                rowSearchParams += `section=${curVal?.name in redirectMappings ? redirectMappings[curVal?.name] : curVal?.name}`;
            }

            // console.log('subRowsConfig', subRowConfig);

            rowConfigs[index] = {
                isBold: curVal?.isBold,
                isLambdaLogic: curVal?.isLambdaLogic,
                isTitle: curVal?.isTitle,
                isPercentage: percentageColumns.includes(curVal?.name) ? true : false,
                isEditable: categoryObj[curVal?.id] != undefined ? true : false,
                redirectLink: categoryObj[curVal?.id] != undefined ? `/pnltracker/edit/${categoryObj[curVal?.id]['feTab'].replace(/\s+/g, '-').toLowerCase()}${rowSearchParams}` : null,
                isMissingCost: Object.keys(rowValuesObj).length == 0 && !curVal.isExpandable && !curVal.isTitle && !curVal.isBold || (rowIsAllNull && !curVal.isExpandable && !curVal.isTitle && !curVal.isBold) ? true : false,
                subRowConfig
            }

            return prev;
        }, [])

        console.log('bodyData', formattedBodyData)
        const tableColumns = tableData?.months.map((item, index) => {
            // console.log('item', item)
            return columnHelper.accessor(item as any, {
                id: item,
                header: item ? (item as string) : " ",
                cell: (info) => info.renderValue(),
                meta: {
                    totalColumns: false,
                    borderRight: !(index),
                }
            });
        });

        const titleColumn = columnHelper.accessor('title' as any, {
            id: 'title',
            header: 'title',
            cell: (info) => info.renderValue()
        });
        const extraColumns = [
            {
                accessor: 'total3Months',
                headerTitle: 'Last 3 Months',
            }, {
                accessor: 'total',
                headerTitle: 'Last 12 Months',
            }]
        const formattedExtraColumns = extraColumns.map((item, index) => {
            return columnHelper.accessor(item.accessor as any, {
                id: item.accessor,
                header: item.headerTitle,
                cell: (info) => info.renderValue(),
                meta: {
                    totalColumns: true,
                    borderRight: item.accessor == 'total3Months' ? true : false
                }
            });
        });

        const countinLakhsPercentage = (countInLakhs / totalCount) * 100;
        // console.log('countInLakhsPercentage', countinLakhsPercentage, countInLakhs, countInThousands, totalCount);

        console.log('tableColumns', tableColumns)
        const allTableColumns = [titleColumn, ...formattedExtraColumns, ...tableColumns];
        // console.log('allTableColumns', allTableColumns)
        setExpanded(expandedInitialState);

        const unitFormat = countinLakhsPercentage > 20 ? pnlFormat.lakhs : pnlFormat.thousands;
        handlePnlFormatChange(unitFormat);
        return {
            body: formattedBodyData,
            headers: allTableColumns,
            rowConfigs: rowConfigs,
            pnlFormat: unitFormat
        }
    }, [tableData]);

    // console.log('rowConfig', formatTableData.rowConfigs);
    // console.log('bodyData', formatTableData.body)

    const table = useReactTable({
        data: formatTableData.body,
        columns: formatTableData.headers,
        onExpandedChange: setExpanded,
        getCoreRowModel: getCoreRowModel(),
        getSubRows: (row: any) => row.subRows,
        state: {
            expanded: expanded
        }
    });

    // Function to scroll to bottom
    const scrollToBottom = () => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTo({
                top: tableContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    const generateDummyRows = useCallback(() => {
        const dummyArr = new Array(15).fill("test");
        return <tr>
            {dummyArr.map((item, index) => {
                return (
                    <td className={`${styles.dummyRow} ${index == 0 && showShadow ? styles.separator : ''} ${index == 3 ? styles.borderRight : ''}  ${index == 1 || index == 2 ? styles.totalColumn : ''}`}>
                        <div className={styles.bodyRowRootContainer}>
                        </div>
                    </td>
                )
            })}
        </tr>
    }, [showShadow])


    // console.log('generateDummyRows', generateDummyRows)

    return (
        <>
            <div ref={tableContainerRef} className={styles.tableContainer}>
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup, groupIndex) => {
                            return <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header, headerIndex) => {
                                    // const isLastIndex = 
                                    const totalTypeColumn = header?.column?.columnDef?.meta && header?.column?.columnDef?.meta['totalColumns'] ? header?.column?.columnDef?.meta['totalColumns'] : false;
                                    const borderRight = header?.column?.columnDef?.meta && header?.column?.columnDef?.meta['borderRight'] ? header?.column?.columnDef?.meta['borderRight'] : false;
                                    return (
                                        <>
                                            {headerIndex != 0 ?
                                                <th className={totalTypeColumn ? `${styles.totalColumns} ${styles.growthWidth}` : borderRight ? styles.borderRight : borderRight}>
                                                    <div className={styles.headerTitle}>
                                                        {header.isPlaceholder
                                                            ? null
                                                            : header?.column?.columnDef?.meta['totalColumns'] ? `${header.column.columnDef.header}` : moment(header.column.columnDef.header as string, "MM-YYYY").format("MMM YYYY")}
                                                        {/* {
                                                            borderRight ?
                                                                <div className={`${styles.growthContent} ${styles.growthOpacityZero}`}>
                                                                    <HiArrowNarrowDown />
                                                                    <p className={styles.number}></p>
                                                                </div> : null
                                                        } */}
                                                    </div>
                                                </th> : <th ref={headerIndex == 0 ? firstColumnRef : null} className={showShadow ? styles.separator : ''}></th>
                                            }
                                        </>

                                    )
                                })}
                            </tr>
                        })}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows?.map((row, index) => {
                            const rowConfig = formatTableData.rowConfigs[index];
                            const isTitle = rowConfig['isTitle'];
                            const rowIsMissingCost = rowConfig['isMissingCost'];
                            const rowEditable = rowConfig['isEditable'];
                            const rowRedirectLink = rowConfig['redirectLink'];
                            const isBold = rowConfig['isBold'];
                            const subRowConfig = rowConfig['subRowConfig'];
                            const isLambdaLogic = rowConfig['isLambdaLogic'] && !ignoreDarkenBorder.includes(row?.original?.title) ? true : false;
                            const isSeparatorRow = separatorRows.includes(row?.original?.title);
                            const higlightRows = highlightRows.includes(row?.original?.title);
                            const addMissingCostParams = applyCostMissingParams.has(row?.original?.title);
                            return (
                                <>
                                    {isSeparatorRow ?
                                        <>
                                            {generateDummyRows()}
                                        </>
                                        : null}
                                    <tr key={row.id}>
                                        {row.getVisibleCells().map((cell, cellIndex) => {
                                            const isLastIndex = cellIndex == row.getVisibleCells()?.length - 1;
                                            const totalColumns = cellIndex == 1 || cellIndex == 2 ? true : false;
                                            const borderRight = cellIndex == 3;
                                            const showGrowthValues = cellIndex == 3 || cellIndex == 1
                                            // console.log('growth', growth)
                                            let growthNumberInitial = 0
                                            if (growth[row?.original?.title]) {
                                                const headerTemp: any = cell?.column?.columnDef?.header
                                                if (Object.keys(growth[row?.original?.title]).includes(headerTemp)) {
                                                    growthNumberInitial = growth[row?.original?.title][headerTemp]
                                                }
                                            }

                                            const showGrowth = (percentageColumns.includes(row?.original?.title) || Math.abs(growthNumberInitial) > 20) && growth[row?.original?.title] && (borderRight || showGrowthValues)
                                            const growthNumber = growthNumberInitial
                                            const isGrowthPositive = growthNumber >= 0

                                            const isMonthGrowthLast = row?.original?.title == PnlFsNames.percGrowth && row.getVisibleCells()?.length - 1 == cellIndex;
                                            // if(borderRight) {
                                            //     console.log(row?.original?.title, growth[row?.original?.title])
                                            // }
                                            return (
                                                <>
                                                    <td className={`${isTitle ? styles.isTitle : ''} ${higlightRows ? styles.higlightRows : ''} ${isSeparatorRow ? styles.separatorRow : ''} ${totalColumns ? styles.totalColumn : ''} ${borderRight ? styles.borderRight : ''} ${cellIndex == 0 && showShadow ? styles.separator : ''} ${rowIsMissingCost && cellIndex == 0 ? styles.missingCostRowFirstColumn : rowIsMissingCost ? styles.missingCostRow : ''} ${showGrowth ? styles.growthRootContainer : ''}`}>
                                                        <div className={`${styles.bodyRowRootContainer} ${cellIndex == 0 && rowIsMissingCost ? styles.missingInnerContainer : ''}`}>

                                                            <div className={`${styles.bodyRowContainer} ${cellIndex != 0 ? styles.value : ''}`}>
                                                                {row.getCanExpand() && cellIndex == 0 ?
                                                                    <button className={styles.expandBtn} onClick={row.getToggleExpandedHandler()}>
                                                                        <div className={`${styles.iconContainer} ${!row.getIsExpanded() ? styles.collapsed : ''}`}>
                                                                            <MdArrowDropDown />
                                                                        </div>

                                                                    </button> : null}
                                                                {cellIndex == 0 && rowIsMissingCost && cellIndex == 0 ?
                                                                    <OverlayTrigger
                                                                        placement="right"
                                                                        delay={{ show: 100, hide: 100 }}
                                                                        overlay={(props) =>
                                                                            RenderSideBarTooltip(
                                                                                props,
                                                                                `${row?.original.title} is missing.`
                                                                            )
                                                                        }
                                                                    >
                                                                        <div className={`${styles.missingCostIcon} ${styles.error}`}>
                                                                            <AlertIcon />
                                                                        </div>
                                                                    </OverlayTrigger>


                                                                    : null}
                                                                <div className={`${styles.bodyRow} ${!row.getCanExpand() && cellIndex == 0 ? styles.noButton : ''} ${isBold || isLastIndex ? styles.bold : ''} ${Number(cell.renderValue()) < 0 ? styles.negativeValue : ''}`}>
                                                                    <div className={styles.cellDiv}>

                                                                        {rowIsMissingCost && cellIndex != 0 ?
                                                                            <div className={styles.missingIconValue}>
                                                                                <ErrorIcon />
                                                                            </div>
                                                                            : isTitle && isLastIndex ? "" : rowConfig['isPercentage'] && cellIndex != 0 ? cell.renderValue() || cell.renderValue() == 0 && (!isMonthGrowthLast) ? `${formatNumberWithComma(cell.renderValue(), 1)}%` : '' : formatNumberWithCommasAndUnits(cell.renderValue(), formatTableData.pnlFormat)}
                                                                        {showGrowthValues ? <div className={`${styles.growthContent} ${!showGrowth ? styles.growthOpacityZero : ''} ${isGrowthPositive ? styles.positive : ''}`}>
                                                                            {
                                                                                isGrowthPositive ?
                                                                                    <HiArrowNarrowUp /> :
                                                                                    <HiArrowNarrowDown />
                                                                            }
                                                                            <p className={styles.number}>{growthNumber}%</p>
                                                                        </div> : null}
                                                                    </div>
                                                                </div>

                                                                {cellIndex == 0 && iBtnColumns[row?.original?.title] != undefined ?
                                                                    <OverlayTrigger
                                                                        placement={"right"}
                                                                        delay={{ show: 100, hide: 100 }}
                                                                        overlay={(props) =>
                                                                            RenderIBtnTooltip(
                                                                                props,
                                                                                iBtnColumns[row?.original?.title]['title'],
                                                                                iBtnColumns[row?.original?.title]['subTitle']
                                                                            )
                                                                        }
                                                                    >
                                                                        <div className={styles.missingCostIcon}>
                                                                            <InfoIcon />
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                    : null}

                                                                {rowEditable && rowRedirectLink && !rowIsMissingCost && !row.getCanExpand() && cellIndex == 0 ? <div className={styles.editIconContainer} onClick={() => {
                                                                    // console.log('REDIRECT LINK', rowRedirectLink + `${missingProductDetails.missingCostCount > 0 ? '&costMissing=true' : ''}`)
                                                                    navigate(rowRedirectLink + `${missingProductDetails.missingCostCount > 0 && addMissingCostParams ? '&costMissing=true' : ''}`)
                                                                }}>
                                                                    <PencilIcon />
                                                                </div> : null}






                                                                {rowIsMissingCost && cellIndex == 0 ?
                                                                    <div style={{ marginLeft: "auto", display: "flex" }}>
                                                                        <MeritoButton icon={<AddIcon />} size="sm" type='inverse' btnText='Add' onClick={() => {
                                                                            navigate(rowRedirectLink)
                                                                        }} />
                                                                    </div> : null}
                                                            </div>

                                                        </div>

                                                    </td >
                                                    {/* {!rowIsMissingCost || cellIndex == 0 ?
                                                    :
                                                    cellIndex == 1 ? <td className={`${styles.missingCost} missing-costs-row`} colSpan={13}>
                                                        <div className={styles.missingCostContainer}
                                                        >
                                                            <div className={styles.leftContainer}>
                                                                <div className={styles.missingCostText}>
                                                                    Add {row?.original?.title}
                                                                </div>
                                                            </div>
                                                            <div className={styles.rightContainer}>
                                                                <MeritoButton type='secondary-2' btnText='Add Data' onClick={() => { }} />
                                                            </div>
                                                        </div>
                                                    </td> : null
                                                } */}
                                                </>
                                            )
                                        })}
                                    </tr >
                                    {
                                        row.getIsExpanded() && row.subRows.length && row.subRows.map((subRow, subRowIndex) => {

                                            return (
                                                <tr key={`${subRow}`}>
                                                    {subRow.getAllCells().map((cell, subRowCellIndex) => {
                                                        const subRowLastIndex = subRowCellIndex == subRow.getAllCells()?.length - 1;
                                                        const subRowEditable = subRowConfig[subRowIndex] != undefined ? subRowConfig[subRowIndex]['isEditable'] : false;
                                                        const redirectLink = subRowConfig[subRowIndex] != undefined ? subRowConfig[subRowIndex]['redirectLink'] : false;
                                                        const dataAvailableError = subRowConfig[subRowIndex]['dataAvailableError'];
                                                        const totalColumns = subRowCellIndex == 1 || subRowCellIndex == 2 ? true : false;
                                                        const borderRight = subRowCellIndex == 3 || subRowCellIndex == 1;
                                                        return (
                                                            <>
                                                                <td key={cell.id} className={`${styles.subRow} ${totalColumns ? styles.totalColumn : ''} ${borderRight ? styles.borderRight : ''} ${subRowCellIndex == 0 && showShadow ? styles.separator : ''}`}>
                                                                    <div className={`${styles.subRowRootContainer} ${dataAvailableError ? styles.dataAvailableError : ''}`}>
                                                                        <div className={`${styles.subRowContainer} ${subRowLastIndex ? styles.isBold : ''} ${borderRight ? styles.gapForGrowth : ''} ${subRowCellIndex != 0 ? styles.alignRight : ''}`}>
                                                                            <div className={styles.valueContainer}>
                                                                                {formatNumberWithCommasAndUnits(cell.renderValue(), formatTableData.pnlFormat)}
                                                                            </div>


                                                                            {subRowEditable && subRowCellIndex == 0 ? <span className={styles.editIcon} onClick={() => {
                                                                                navigate(redirectLink)
                                                                            }}><PencilIcon /></span> : null}
                                                                            {
                                                                                borderRight ?
                                                                                    <div style={{ width: '1.9375rem' }}></div> : null
                                                                            }
                                                                        </div>
                                                                        {dataAvailableError && subRowCellIndex == 0 ?
                                                                            <div className={`${styles.dataAvailableContainer} ${borderRight ? styles.gapForGrowth : ''}`}>
                                                                                <div className={styles.iconContainer}>
                                                                                    <DotIcon />
                                                                                </div>
                                                                                <div className={styles.dataAvailableText}>
                                                                                    {dataAvailableError}
                                                                                </div>
                                                                                {
                                                                                    borderRight ?
                                                                                        <div style={{ width: '1.9375rem' }}></div> : null
                                                                                }
                                                                            </div> : null
                                                                        }
                                                                    </div>

                                                                </td>
                                                            </>
                                                        )
                                                    })}
                                                </tr>
                                            )
                                        })
                                    }
                                </>
                            )
                        })}
                    </tbody>
                </table>



            </div >
            <div className={styles.scrollToBottomBtn}>
                <button onClick={scrollToBottom} className={styles.btnScrollToBottom}>
                    <ScrollToBottomIcon />
                </button>
            </div>
        </>
    );
}

export default FinanceTable;